import React, { useState, useEffect } from "react";
import { FaCheck, FaPaperPlane } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import KeypadModal from "./KeypadModal";
import axios from "axios";
import BASE_URL from "../utils/Urls";
import ConfirmModal from "./ConfirmModal";

const defaultSlabs = [
  { name: "incentives", credits: ["20", "35", "50", "55"], amounts: ["1700", "3150", "5250", "6050"] },
  { name: "part-time", credits: ["60", "85", "105", "115"], amounts: ["6500", "14875", "19425", "22425"] },
  { name: "full-time", credits: ["120"], amounts: ["23000"] },
];
const defaultIncentive = { step_credits: "8", step_amount: "1760" };

const cloneSlabs = (s) => s.map((x) => ({ ...x, credits: [...x.credits], amounts: [...x.amounts] }));

const normalizeLoadedSlabs = (slabs) =>
  Array.isArray(slabs) && slabs.length
    ? slabs.map((s) => ({
        name: String(s?.name ?? ""),
        credits: Array.isArray(s?.credits) ? s.credits.map(String) : [],
        amounts: Array.isArray(s?.amounts) ? s.amounts.map(String) : [],
      }))
    : cloneSlabs(defaultSlabs);

const prevLadderValue = (slabs, field, slabIndex, cellIndex) => {
  if (cellIndex > 0) return Number(slabs[slabIndex]?.[field]?.[cellIndex - 1]) || 0;
  for (let s = slabIndex - 1; s >= 0; s--) {
    const arr = slabs[s]?.[field] || [];
    if (arr.length) return Number(arr[arr.length - 1]) || 0;
  }
  return null;
};

const cellInvalid = (slabs, field, slabIndex, cellIndex) => {
  const prev = prevLadderValue(slabs, field, slabIndex, cellIndex);
  if (prev === null) return false;
  return (Number(slabs[slabIndex]?.[field]?.[cellIndex]) || 0) <= prev;
};

const findSlabError = (slabs) => {
  for (const field of ["amounts", "credits"]) {
    for (let si = 0; si < (slabs || []).length; si++) {
      const arr = slabs[si]?.[field] || [];
      for (let ci = 0; ci < arr.length; ci++) {
        if (cellInvalid(slabs, field, si, ci)) {
          return `${slabs[si].name} — ${field}: each value must be greater than the previous stage (across all slabs).`;
        }
      }
    }
  }
  return null;
};

const ProgressPopup = ({ onClose }) => {
  const [showMain, setShowMain] = useState(true);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [slabs, setSlabs] = useState(cloneSlabs(defaultSlabs));
  const [incentive, setIncentive] = useState({ ...defaultIncentive });

  const [showAppliesTo, setShowAppliesTo] = useState(false);
  const [applyType, setApplyType] = useState("global");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/admin/salary-config`);
        const c = res.data?.config;
        if (alive && c) {
          setSlabs(normalizeLoadedSlabs(c.slabs));
          setIncentive({
            step_credits: String(c.incentive?.step_credits ?? defaultIncentive.step_credits),
            step_amount: String(c.incentive?.step_amount ?? defaultIncentive.step_amount),
          });
        }
      } catch (err) {
        console.error("❌ Failed to load salary config", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!showAppliesTo || (groups.length && promoters.length)) return;
    let alive = true;
    (async () => {
      try {
        const [gRes, uRes] = await Promise.all([
          axios.get(`${BASE_URL}/groups/`),
          axios.get(`${BASE_URL}/users/`),
        ]);
        if (!alive) return;
        const gs = Array.isArray(gRes.data?.result) ? gRes.data.result : [];
        setGroups([...new Set(gs.map((g) => g.group).filter(Boolean))]);
        const us = Array.isArray(uRes.data?.users) ? uRes.data.users : [];
        setPromoters(us);
      } catch (err) {
        console.error("❌ Failed to load groups/promoters", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [showAppliesTo, groups.length, promoters.length]);

  const handleCellChange = (slabIndex, field, cellIndex, value) => {
    const next = String(value).replace(/[^0-9]/g, "");
    setSlabs((prev) => {
      const updated = cloneSlabs(prev);
      updated[slabIndex][field][cellIndex] = next;
      return updated;
    });
  };

  const handleIncentiveChange = (field, value) => {
    setIncentive((prev) => ({ ...prev, [field]: String(value).replace(/[^0-9]/g, "") }));
  };

  const save = async () => {
    const err = findSlabError(slabs);
    if (err) {
      setShowConfirm(false);
      alert(`⚠️ ${err}`);
      return;
    }
    const applies_to =
      applyType === "group"
        ? { type: "group", keys: selectedGroups }
        : applyType === "individual"
        ? { type: "individual", keys: selectedUsers }
        : { type: "global", keys: [] };
    try {
      const payload = {
        slabs,
        incentive: {
          step_credits: Number(incentive.step_credits) || 0,
          step_amount: Number(incentive.step_amount) || 0,
        },
        applies_to,
      };
      await axios.put(`${BASE_URL}/admin/salary-config`, payload);
      setShowConfirm(false);
      setShowMain(false);
      onClose?.();
    } catch (err) {
      console.error("❌ Failed to save salary config", err);
      alert(`❌ ${err?.response?.data?.message || "Failed to save salary slabs. Please try again."}`);
      setShowConfirm(false);
    }
  };

  const slabError = findSlabError(slabs);

  const toggleInList = (setter) => (val) =>
    setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  const toggleGroup = toggleInList(setSelectedGroups);
  const toggleUser = toggleInList(setSelectedUsers);
  const applyValid =
    applyType === "global" ||
    (applyType === "group" && selectedGroups.length > 0) ||
    (applyType === "individual" && selectedUsers.length > 0);
  const userQuery = userSearch.trim().toLowerCase();
  const filteredPromoters = userQuery
    ? promoters.filter((u) =>
        `${u.employer_name || ""} ${u.phone_number || ""} ${u.user_id || ""} ${u.group || ""}`
          .toLowerCase()
          .includes(userQuery)
      )
    : promoters;

  return (
    <>
      {showMain && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-start z-50 overflow-y-auto py-8">
          <div
            className="bg-white rounded-lg shadow-lg flex flex-col relative animate-slideUp my-auto"
            style={{ width: "560px", maxHeight: "85vh" }}
          >
            <button
              onClick={() => {
                setShowMain(false);
                onClose?.();
              }}
              className="absolute top-3 right-5 text-red-600 hover:text-red-800 z-10"
            >
              <IoClose className="text-2xl" />
            </button>

            <div className="px-6 pt-5 pb-3">
              <h2 className="text-center font-bold text-xl text-gray-800">Salary slabs</h2>
              <p className="text-center text-xs text-gray-400">
                Credit thresholds &amp; salary amounts — choose who they apply to next
              </p>
            </div>

            <div className="px-6 pb-4 overflow-y-auto" style={{ flex: 1 }}>
              {slabs.map((slab, slabIndex) => (
                <div
                  key={slabIndex}
                  className="border border-red-500 rounded-lg p-4 mb-4 relative"
                >
                  <div className="text-left font-bold text-sm text-red-500 capitalize mb-1">
                    {slab.name}
                  </div>
                  <div className="text-left text-xs font-semibold text-gray-400 mb-2">Amounts</div>

                  <div className="flex items-start w-full relative justify-between">
                    {slab.credits.length > 1 && (
                      <div className="absolute h-[3px] bg-green-500 top-[34px] left-[28px] right-[28px]"></div>
                    )}

                    {slab.credits.map((_, cellIndex) => (
                      <div key={cellIndex} className="flex flex-col items-center z-10 flex-1">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={slab.amounts[cellIndex] ?? ""}
                          onChange={(e) =>
                            handleCellChange(slabIndex, "amounts", cellIndex, e.target.value)
                          }
                          className={`text-base font-bold text-center w-16 border-b focus:outline-none mb-1 ${
                            cellInvalid(slabs, "amounts", slabIndex, cellIndex)
                              ? "text-red-600 border-red-500"
                              : "text-black border-gray-300"
                          }`}
                        />

                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] bg-green-500 text-white my-1">
                          <FaCheck />
                        </div>

                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={slab.credits[cellIndex] ?? ""}
                          onChange={(e) =>
                            handleCellChange(slabIndex, "credits", cellIndex, e.target.value)
                          }
                          className={`text-base text-center w-16 border-b focus:outline-none mt-1 ${
                            cellInvalid(slabs, "credits", slabIndex, cellIndex)
                              ? "text-red-600 border-red-500"
                              : "text-gray-700 border-gray-300"
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="text-left text-xs font-semibold text-gray-400 mt-2">Credits</div>
                </div>
              ))}

              <div className="border border-green-500 rounded-lg p-4 mb-2">
                <div className="text-left font-bold text-sm text-green-600 mb-2">
                  Incentive (above full-time max)
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex flex-col text-xs text-gray-500 flex-1 min-w-0">
                    Every (credits)
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={incentive.step_credits}
                      onChange={(e) => handleIncentiveChange("step_credits", e.target.value)}
                      className="mt-1 w-full text-base font-bold text-black text-center border border-gray-300 rounded px-2 py-1 focus:outline-none"
                    />
                  </label>
                  <span className="text-lg font-bold text-gray-400 mt-4">→ +</span>
                  <label className="flex flex-col text-xs text-gray-500 flex-1 min-w-0">
                    Incentive (amount)
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={incentive.step_amount}
                      onChange={(e) => handleIncentiveChange("step_amount", e.target.value)}
                      className="mt-1 w-full text-base font-bold text-black text-center border border-gray-300 rounded px-2 py-1 focus:outline-none"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center py-4 border-t border-gray-100">
              {slabError && (
                <p className="text-center text-xs text-red-500 mb-2 px-6">{slabError}</p>
              )}
              <button
                onClick={() => {
                  if (slabError) return;
                  setShowAppliesTo(true);
                }}
                disabled={!!slabError}
                className={`flex items-center justify-center gap-2 bg-gradient-to-r from-purple-800 to-pink-600 text-white font-bold py-3 px-10 rounded-full shadow-lg ${
                  slabError ? "opacity-50 cursor-not-allowed" : ""
                }`}
                style={{ border: "2px solid gold" }}
              >
                CHANGE
                <FaPaperPlane className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showAppliesTo && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-start z-50 overflow-y-auto py-8">
          <div
            className="bg-white rounded-lg shadow-lg flex flex-col relative animate-slideUp my-auto"
            style={{ width: "480px", maxHeight: "85vh" }}
          >
            <button
              onClick={() => setShowAppliesTo(false)}
              className="absolute top-3 right-5 text-red-600 hover:text-red-800 z-10"
            >
              <IoClose className="text-2xl" />
            </button>

            <div className="px-6 pt-5 pb-3">
              <h2 className="text-center font-bold text-xl text-gray-800">Apply salary slabs to</h2>
              <p className="text-center text-xs text-gray-400">
                Choose who these slabs apply to (individual overrides beat group, group beats the global default)
              </p>
            </div>

            <div className="px-6 pb-4 overflow-y-auto" style={{ flex: 1 }}>
              {[
                { key: "global", label: "All promoters", hint: "Updates the global default for everyone" },
                { key: "group", label: "Specific group(s)", hint: "Everyone in the chosen group(s)" },
                { key: "individual", label: "Specific individual(s)", hint: "Only the promoters you pick" },
              ].map((opt) => (
                <label
                  key={opt.key}
                  className={`flex items-start gap-3 border rounded-lg p-3 mb-2 cursor-pointer ${
                    applyType === opt.key ? "border-purple-600 bg-purple-50" : "border-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="applyType"
                    className="mt-1"
                    checked={applyType === opt.key}
                    onChange={() => setApplyType(opt.key)}
                  />
                  <div>
                    <div className="text-sm font-bold text-gray-800">{opt.label}</div>
                    <div className="text-xs text-gray-400">{opt.hint}</div>
                  </div>
                </label>
              ))}

              {applyType === "group" && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-400 mb-2">
                    Groups {selectedGroups.length ? `(${selectedGroups.length} selected)` : ""}
                  </div>
                  {groups.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">No groups found.</div>
                  ) : (
                    <div className="max-h-52 overflow-y-auto flex flex-col gap-1">
                      {groups.map((g) => (
                        <label key={g} className="flex items-center gap-2 text-sm text-gray-700 py-1">
                          <input
                            type="checkbox"
                            checked={selectedGroups.includes(g)}
                            onChange={() => toggleGroup(g)}
                          />
                          <span className="capitalize">{g}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {applyType === "individual" && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-400">
                      Promoters {selectedUsers.length ? `(${selectedUsers.length} selected)` : ""}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search name / phone / group…"
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-2 focus:outline-none"
                  />
                  {promoters.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">Loading promoters…</div>
                  ) : (
                    <div className="max-h-52 overflow-y-auto flex flex-col gap-1">
                      {filteredPromoters.map((u) => (
                        <label
                          key={u.user_id}
                          className="flex items-center gap-2 text-sm text-gray-700 py-1"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(String(u.user_id))}
                            onChange={() => toggleUser(String(u.user_id))}
                          />
                          <span className="flex-1 min-w-0 truncate">
                            {u.employer_name || u.phone_number || u.user_id}
                            <span className="text-xs text-gray-400">
                              {u.phone_number ? ` · ${u.phone_number}` : ""}
                              {u.group ? ` · ${u.group}` : ""}
                            </span>
                          </span>
                        </label>
                      ))}
                      {filteredPromoters.length === 0 && (
                        <div className="text-xs text-gray-400 py-2">No matches.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-3 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowAppliesTo(false)}
                className="px-6 py-2 rounded-full border border-gray-300 text-gray-600 font-semibold"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (!applyValid) return;
                  setShowAppliesTo(false);
                  setShowKeypad(true);
                }}
                disabled={!applyValid}
                className={`flex items-center justify-center gap-2 bg-gradient-to-r from-purple-800 to-pink-600 text-white font-bold py-2 px-8 rounded-full shadow-lg ${
                  !applyValid ? "opacity-50 cursor-not-allowed" : ""
                }`}
                style={{ border: "2px solid gold" }}
              >
                CONTINUE
                <FaPaperPlane className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showKeypad && (
        <KeypadModal
          type="secondary"
          onGoClick={() => {
            setShowKeypad(false);
            setShowConfirm(true);
          }}
          onClose={() => setShowKeypad(false)}
        />
      )}

      {showConfirm && <ConfirmModal onYes={save} onNo={() => setShowConfirm(false)} />}
    </>
  );
};

export default ProgressPopup;
